// Transport in the console (docs/spec/transport.md slice 1): the organisation's routes, their stops
// and riders, and its vehicles. Organisation administrators (transport:manage).
import { useEffect, useState, type ReactElement, type ReactNode } from "react";
import { Badge, Banner, Button, Checkbox, Dialog, DialogClose, EmptyState, IconButton, Select, Sheet, Table, Tabs, TextField } from "@vavr/ui";
import "./transport.css";
import { BusPanel } from "./staff.tsx";
import { BusCard, BusPage, RidePage, RidesPage } from "./guardian.tsx";
import { Runs } from "./runs.tsx";
import { Compliance, Dispatch, Drivers, SchoolTrips, ServiceCalendar, Timetable, Trips } from "./schedule.tsx";
import { Planner, Shifts, TimetableEditor } from "./planner.tsx";
import { MySchedule } from "./staff-schedule.tsx";
import { BusDetail, BusMap, busStatus, busText, EXCEPTION, FleetMap, FleetSummary, useFleet, useRunRecord } from "./fleet.tsx";
import { api, fullName, Link, MapView, navigate, time, usePath, useQuery, type MapMarker, type UiPart } from "@vavr/screens/shared";
import { T } from "@vavr/screens/teacher";
import { ConfirmButton, LoadError, Loading, Toolbar, useAction, useConsole, useGet } from "@vavr/screens/console";

interface Vehicle { id: string; name: string; plate: string | null; seats: number | null }
interface RouteRow { id: string; name: string; kind: "am" | "pm" | "trip"; vehicle_id: string | null; driver_account_id: string | null; days: number[]; stops: number; riders: number }
/** Who may drive a route: `driver` marks a bus driver role, the rest are staff or dispatchers who may ride. */
interface Driver { id: string; name: string; driver: boolean }
interface Overview { kind: "school" | "operator"; vehicles: Vehicle[]; routes: RouteRow[]; sites: { id: string; name: string }[]; drivers: Driver[] }
interface Contract { id: string; status: "active" | "ended"; created_at: number; ended_at: number | null; site_id: string; school: string; operator?: string; school_org?: string }
interface Stop { id?: string; name: string; time: string | null; site_id: string | null; lat?: number | null; lng?: number | null }
interface Rider { id: string; site_id: string; child_id: string; stop_id: string; meet_required: boolean; meet_by?: string; meet_reason?: string | null; child: { first: string; last: string } | null }

const KIND: Record<RouteRow["kind"], string> = { am: "To school", pm: "From school", trip: "Trip" };
const DAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function useTransport() {
  const { site } = useConsole();
  const base = `/api/v1/orgs/${site.org_id}/transport`;
  return { base, overview: useGet<Overview>(base) };
}

const TRANSPORT_PAGES = [
  { page: "live", path: "transport", label: "Live transport", icon: "bus", description: "Follow today's buses, check exceptions and review completed runs." },
  { page: "planner", path: "transport/planner", label: "Shifts & planner", icon: "calendar", description: "Plan the week: shifts by driver and day. Drag a shift to another driver, fill open shifts, copy a week's changes." },
  { page: "dispatch", path: "transport/dispatch", label: "Daily dispatch", icon: "list", description: "One day, run by run: change a run's driver or bus, cancel a run, and resolve what needs attention." },
  { page: "drivers", path: "transport/drivers", label: "Drivers & time off", icon: "user", description: "Review planned weekly hours, edit driver availability and approve time off." },
  { page: "timetable", path: "transport/timetable", label: "Timetables", icon: "clock", description: "Each route's week at a glance, and its days and stop times to edit. Open a run for its printable sheet." },
  { page: "calendar", path: "transport/calendar", label: "Service calendar", icon: "note", description: "Manage closures, early releases and late starts for your own routes." },
  { page: "trips", path: "transport/trips", label: "Trips", icon: "route", description: "Review school trip requests and organize one-off journeys, vehicles and drivers." },
  { page: "fleet", path: "transport/fleet", label: "Fleet & routes", icon: "settings", description: "Maintain routes, vehicles, bus changes and compliance records." },
] as const;
type TransportPage = (typeof TRANSPORT_PAGES)[number]["page"];

/** Related views stay within a page, with their selection in the URL for reload and Back. */
function TransportViews({ label, items }: { label: string; items: { value: string; label: string; content: ReactNode }[] }) {
  const path = usePath();
  const query = useQuery();
  const params = new URLSearchParams(query);
  const view = params.get("view");
  const value = items.some((i) => i.value === view) ? view! : items[0]!.value;
  return <Tabs label={label} value={value} onValueChange={(next) => { const p = new URLSearchParams(query); p.set("view", next); navigate(`${path}?${p}`); }} items={items} />;
}

/** Each management workflow is a real console page; only related live/fleet views use tabs. */
function Transport({ page = "live" }: { page?: TransportPage }) {
  const { base, overview } = useTransport();
  const { site } = useConsole();
  if (overview.loading) return <Loading />;
  if (overview.error) return <LoadError error={overview.error} retry={overview.reload} />;
  const o = overview.data!;
  const operator = o.kind === "operator";
  const meta = TRANSPORT_PAGES.find((p) => p.page === page)!;
  if (page === "live" && !operator && !o.routes.length && !o.vehicles.length) return (
    <EmptyState icon="bus" title="Your school runs no buses of its own"
      description="Bus companies, your school's days and trip requests are on the Buses page. Set up vehicles and routes only if your school drives its own buses."
      action={<span className="pmu-row"><Button onClick={() => navigate("/console/buses")}>Open Buses</Button><Button variant="secondary" onClick={() => navigate("/console/transport/fleet?view=vehicles")}>We run our own buses</Button></span>} />
  );
  const content = page === "planner" ? <TransportViews key="planner" label="Planner" items={[
      { value: "week", label: "Week planner", content: <Planner base={base} /> },
      { value: "shifts", label: "Shifts", content: <Shifts base={base} /> },
    ]} />
    : page === "dispatch" ? <Dispatch base={base} />
    : page === "drivers" ? <Drivers base={base} />
    : page === "timetable" ? <TransportViews key="timetable" label="Timetables" items={[
      { value: "week", label: "Week view", content: <Timetable base={base} /> },
      { value: "edit", label: "Edit times and days", content: <TimetableEditor base={base} /> },
    ]} />
    : page === "calendar" ? <ServiceCalendar base={base} />
    : page === "trips" ? <Trips base={base} vehicles={o.vehicles} drivers={o.drivers} />
    : page === "fleet" ? <TransportViews key="fleet" label="Fleet" items={[
      { value: "routes", label: `Routes (${o.routes.length})`, content: <Routes base={base} o={o} /> },
      { value: "vehicles", label: `Vehicles (${o.vehicles.length})`, content: <Vehicles base={base} o={o} /> },
      { value: "compliance", label: "Compliance", content: <Compliance base={base} /> },
      { value: "swaps", label: "Bus changes", content: <Swaps base={base} o={o} /> },
      ...(operator ? [{ value: "contracts", label: "Schools", content: <Contracts base={base} sites={o.sites} /> }] : []),
    ]} /> : <TransportViews key="live" label="Live transport" items={[
      { value: "today", label: "Today", content: <Today base={base} /> },
      { value: "busmap", label: "Bus map", content: <BusMap base={base} tzDefault={site.tz} /> },
      { value: "runs", label: "Run history", content: <Runs base={base} tz={site.tz} /> },
    ]} />;
  const related = page === "planner" ? ["dispatch", "drivers"] : page === "dispatch" ? ["planner", "drivers"] : page === "drivers" || page === "timetable" || page === "calendar" ? ["planner", "dispatch"] : ["planner", "fleet"];
  return (
    <div className="pmu-stack ptr-page">
      <header className="ptr-page__head">
        <div><nav aria-label="Transport breadcrumb" className="ptr-page__crumb"><Link href="/console/transport">Transport</Link><span aria-hidden="true">/</span><span>{meta.label}</span></nav><p>{meta.description}</p></div>
        <nav aria-label="Related transport pages" className="ptr-page__links">{TRANSPORT_PAGES.filter((p) => related.includes(p.page) && p.page !== page).map((p) => <Link key={p.page} href={`/console/${p.path}`}>{p.label}</Link>)}</nav>
      </header>
      {content}
    </div>
  );
}

/**
 * A school office's page for the buses that serve it (site.configure): its bus companies, its own
 * days (every bus serving the school follows them) and its trip requests. Nothing of a bus
 * company's own operation shows here.
 */
function SchoolBuses() {
  const { site, can } = useConsole();
  const orgAdmin = can("transport:manage");
  return (
    <Tabs label="Buses" defaultValue="companies" items={[
      { value: "companies", label: "Bus companies", content: orgAdmin ? <SchoolContracts /> : <CompaniesList siteId={site.id} /> },
      { value: "days", label: "School days", content: <ServiceCalendar base={`/api/v1/sites/${site.id}/transport`} school /> },
      { value: "trips", label: "Trips", content: <SchoolTrips siteId={site.id} /> },
    ]} />
  );
}

function SchoolContracts() {
  const { base, overview } = useTransport();
  if (overview.loading) return <Loading />;
  if (overview.error) return <LoadError error={overview.error} retry={overview.reload} />;
  return <Contracts base={base} sites={overview.data!.sites} />;
}

/** The bus companies serving this school, for an office that may not add or end them. */
function CompaniesList({ siteId }: { siteId: string }) {
  const q = useGet<{ operators: { id: string; name: string }[] }>(`/api/v1/sites/${siteId}/transport/trips`);
  if (q.loading) return <Loading />;
  if (q.error) return <LoadError error={q.error} retry={q.reload} />;
  return (
    <div className="pmu-stack">
      <p className="pmu-muted">Your organization's administrators add and end bus companies.</p>
      <Table caption="Bus companies" rowKey={(k) => k.id} rows={q.data!.operators} empty="No bus company serves this school yet."
        columns={[{ key: "name", header: "Bus company", rowHeader: true, render: (k) => k.name }]} />
    </div>
  );
}

function Routes({ base, o }: { base: string; o: Overview }) {
  const [open, setOpen] = useState<RouteRow | null>(null);
  const vehicle = (id: string | null) => o.vehicles.find((v) => v.id === id)?.name ?? "No vehicle";
  const driver = (id: string | null) => (id ? o.drivers.find((d) => d.id === id)?.name ?? "Unknown" : <Badge tone="warning">No driver: cannot run</Badge>);
  return (
    <div className="pmu-stack">
      <Toolbar actions={<RouteForm base={base} o={o} />} />
      <Table
        caption="Routes" hideCaption rowKey={(r) => r.id} rows={o.routes} empty="No routes yet. Add your vehicles, then a route."
        columns={[
          { key: "name", header: "Route", rowHeader: true, render: (r) => <span className="pmu-stack pmu-c-tight"><button type="button" className="pmu-c-linkbutton" onClick={() => setOpen(r)}>{r.name}</button><span className="pmu-small pmu-muted">{KIND[r.kind]} · {r.days.map((d) => DAY[d]).join(" ")}</span></span> },
          { key: "vehicle", header: "Vehicle", render: (r) => vehicle(r.vehicle_id) },
          { key: "driver", header: "Driver", render: (r) => driver(r.driver_account_id) },
          { key: "stops", header: "Stops", align: "end", render: (r) => r.stops },
          { key: "riders", header: "Riders", align: "end", render: (r) => r.riders },
        ]}
      />
      <Sheet title={open?.name ?? "Route"} description={open ? `${KIND[open.kind]} · ${vehicle(open.vehicle_id)}` : undefined} open={open !== null} onOpenChange={(v) => !v && setOpen(null)}>
        {open && <RouteDetail base={base} o={o} routeId={open.id} onGone={() => setOpen(null)} />}
      </Sheet>
    </div>
  );
}

function RouteForm({ base, o, edit }: { base: string; o: Overview; edit?: RouteRow }) {
  const [open, setOpen] = useState(false);
  const blank = { name: "", kind: "am" as RouteRow["kind"], vehicle_id: o.vehicles[0]?.id ?? null, driver_account_id: null as string | null, days: [1, 2, 3, 4, 5] };
  const [f, setF] = useState(blank);
  const act = useAction();
  const save = () => act.run(() => api(edit ? "PUT" : "POST", edit ? `${base}/routes/${edit.id}` : `${base}/routes`, f), { success: edit ? "Route saved." : "Route added.", invalidate: [base, `${base}/routes/${edit?.id}`] }).then((r) => { if (r) setOpen(false); });
  return (
    <Dialog title={edit ? "Edit the route" : "Add a route"} open={open} onOpenChange={(v) => { setOpen(v); if (v) setF(edit ? { name: edit.name, kind: edit.kind, vehicle_id: edit.vehicle_id, driver_account_id: edit.driver_account_id, days: edit.days } : blank); }}
      trigger={<Button icon={edit ? undefined : "plus"} variant={edit ? "secondary" : "primary"} size={edit ? "sm" : "md"}>{edit ? "Edit" : "Add a route"}</Button>}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!f.name.trim() || !f.days.length} onClick={save}>Save</Button></>}>
      <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (f.name.trim()) void save(); }}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <TextField label="Name" required placeholder="North morning" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Select label="Direction" value={f.kind} onValueChange={(v) => setF({ ...f, kind: (v ?? "am") as RouteRow["kind"] })} options={[{ value: "am", label: "To school (morning)" }, { value: "pm", label: "From school (afternoon)" }, { value: "trip", label: "Trip" }]} />
        <Select label="Vehicle" value={f.vehicle_id ?? ""} onValueChange={(v) => setF({ ...f, vehicle_id: v || null })} options={[{ value: "", label: "No vehicle yet" }, ...o.vehicles.map((v) => ({ value: v.id, label: v.name }))]} />
        <Select label="Driver" hint="The route shows only on its driver's phone (and to dispatchers). Without a driver it cannot run: invite a driver under Staff, then assign them here."
          value={f.driver_account_id ?? ""} onValueChange={(v) => setF({ ...f, driver_account_id: v || null })}
          options={[{ value: "", label: "No driver yet" }, ...[...o.drivers].sort((a, b) => Number(b.driver) - Number(a.driver)).map((d) => ({ value: d.id, label: d.driver ? d.name : `${d.name} (administrator)` }))]} />
        <fieldset className="pmu-row">
          <legend className="pmu-small">Days</legend>
          {DAY.map((d, i) => <Checkbox key={d} label={d} checked={f.days.includes(i)} onCheckedChange={(on) => setF({ ...f, days: on ? [...f.days, i].sort() : f.days.filter((x) => x !== i) })} />)}
        </fieldset>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function RouteDetail({ base, o, routeId, onGone }: { base: string; o: Overview; routeId: string; onGone: () => void }) {
  const path = `${base}/routes/${routeId}`;
  const d = useGet<{ route: RouteRow; stops: (Stop & { id: string })[]; riders: Rider[] }>(path);
  const act = useAction();
  if (d.loading) return <Loading />;
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  const { route, stops, riders } = d.data!;
  return (
    <div className="pmu-stack">
      {act.error && <Banner tone="danger" title={act.error} />}
      <div className="pmu-row">
        <RouteForm base={base} o={o} edit={{ ...route, stops: stops.length, riders: riders.length }} />
        <ConfirmButton size="sm" variant="ghost" danger title={`Remove ${route.name}?`} description="The route, its stops and its riders go from the list. Records already made on it stay." confirmLabel="Remove"
          onConfirm={() => act.run(() => api("DELETE", path), { success: "Route removed.", invalidate: [base] }).then((r) => { if (r) onGone(); })}>Remove the route</ConfirmButton>
      </div>
      <StopsEditor path={path} base={base} o={o} stops={stops} />
      <Riders path={path} o={o} stops={stops} riders={riders} />
    </div>
  );
}

function StopsEditor({ path, base, o, stops }: { path: string; base: string; o: Overview; stops: (Stop & { id: string })[] }) {
  const [rows, setRows] = useState<Stop[] | null>(null);
  const [selected, setSelected] = useState(0);
  const [address, setAddress] = useState("");
  const [found, setFound] = useState<{ address: string; lat: number; lng: number }[] | null>(null);
  const act = useAction();
  const list = rows ?? stops;
  const set = (i: number, s: Partial<Stop>) => setRows(list.map((x, j) => (j === i ? { ...x, ...s } : x)));
  const move = (i: number, by: number) => { const n = [...list]; const [x] = n.splice(i, 1); n.splice(i + by, 0, x!); setRows(n); setSelected(i + by); };
  const save = () => act.run(() => api("PUT", `${path}/stops`, { stops: list.map((s) => ({ ...s, time: s.time || null })) }), { success: "Stops saved.", invalidate: [path, base] }).then((r) => { if (r) setRows(null); });
  const oneSchool = o.sites.length === 1 ? o.sites[0]! : null;
  const place = (lat: number, lng: number) => { if (list[selected]) set(selected, { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }); };
  const find = async () => {
    const r = await act.run(() => api<{ matches: { address: string; lat: number; lng: number }[] }>("GET", `${base}/geocode?q=${encodeURIComponent(address)}`));
    if (!r) return;
    setFound(r.matches);
    if (r.matches.length === 1) { place(r.matches[0]!.lat, r.matches[0]!.lng); setFound(null); }
  };
  const markers: MapMarker[] = list.flatMap((s, i) => (s.lat != null && s.lng != null
    ? [{ id: s.id ?? `new-${i}`, lat: s.lat, lng: s.lng, kind: s.site_id ? "school" as const : "stop" as const, label: s.site_id ? undefined : String(i + 1), title: `${i + 1}. ${s.name || "Stop"}`, onMove: (lat: number, lng: number) => set(i, { lat, lng }) }]
    : []));
  const current = list[selected];
  return (
    <section className="pmu-stack" aria-labelledby="stops-h">
      <h3 id="stops-h" className="pmu-c-h3">Stops, in order</h3>
      {act.error && <Banner tone="danger" title={act.error} />}
      {list.length === 0 && <p className="pmu-muted">No stops yet. Add the pick-up points in order, then the school.</p>}
      <ol className="pmt-stops">
        {list.map((s, i) => (
          <li key={s.id ?? `new-${i}`} className="pmt-stop" data-school={s.site_id ? "" : undefined} data-selected={i === selected ? "" : undefined}>
            <button type="button" className="pmt-stop__n" aria-label={`Place stop ${i + 1} on the map`} aria-pressed={i === selected} onClick={() => setSelected(i)}>{i + 1}</button>
            <TextField label={`Stop ${i + 1} name`} hideLabel placeholder="Oak & 5th" value={s.name} onChange={(e) => set(i, { name: e.target.value })} />
            <TextField label={`Stop ${i + 1} time`} hideLabel type="time" value={s.time ?? ""} onChange={(e) => set(i, { time: e.target.value || null })} />
            {oneSchool
              ? <Checkbox label="School" checked={s.site_id === oneSchool.id} onCheckedChange={(on) => set(i, { site_id: on ? oneSchool.id : null })} />
              : <Select label={`Stop ${i + 1} is`} value={s.site_id ?? "stop"} onValueChange={(v) => set(i, { site_id: v && v !== "stop" ? v : null })} options={[{ value: "stop", label: "A pick-up stop" }, ...o.sites.map((x) => ({ value: x.id, label: x.name }))]} />}
            <span className="pmt-stop__acts">
              <IconButton icon="chevron-up" label={`Move stop ${i + 1} up`} size="sm" disabled={i === 0} onClick={() => move(i, -1)} />
              <IconButton icon="chevron-down" label={`Move stop ${i + 1} down`} size="sm" disabled={i === list.length - 1} onClick={() => move(i, 1)} />
              <IconButton icon="close" label={`Remove stop ${i + 1}`} size="sm" onClick={() => { setRows(list.filter((_, j) => j !== i)); setSelected(0); }} />
            </span>
          </li>
        ))}
      </ol>
      <div className="pmu-row">
        <Button size="sm" variant="secondary" icon="plus" onClick={() => { setRows([...list, { name: "", time: null, site_id: null }]); setSelected(list.length); }}>Add a stop</Button>
        {rows && <Button size="sm" loading={act.busy} disabled={list.some((s) => !s.name.trim())} onClick={save}>Save the stops</Button>}
        {rows && <Button size="sm" variant="ghost" onClick={() => setRows(null)}>Undo changes</Button>}
      </div>
      {current && (
        <div className="pmu-stack">
          <p className="pmu-small pmu-muted">Placing <b>{selected + 1}. {current.name || "the new stop"}</b>{current.lat == null ? ", not on the map yet" : ""}: find its address, click the map, or drag its pin. A stop also learns its place from the bus the first time a child gets on or off there.</p>
          <form className="pmu-row" onSubmit={(e) => { e.preventDefault(); if (address.trim().length >= 5) void find(); }}>
            <TextField label="Address" hideLabel placeholder="1100 Congress Ave, Austin, TX" value={address} onChange={(e) => setAddress(e.target.value)} />
            <Button type="submit" size="sm" variant="secondary" icon="search" loading={act.busy} disabled={address.trim().length < 5}>Find</Button>
          </form>
          {found && (found.length === 0 ? <p className="pmu-small pmu-muted">No US address matched. Check it, or click the map.</p> : (
            <ul className="pmu-stack">{found.map((f) => <li key={f.address}><Button size="sm" variant="ghost" onClick={() => { place(f.lat, f.lng); setFound(null); }}>{f.address}</Button></li>)}</ul>
          ))}
          <MapView label="Stops on the map" markers={markers} onPick={place} height={320} />
        </div>
      )}
    </section>
  );
}

function Riders({ path, o, stops, riders }: { path: string; o: Overview; stops: (Stop & { id: string })[]; riders: Rider[] }) {
  const act = useAction();
  const pickups = stops.filter((s) => !s.site_id);
  return (
    <section className="pmu-stack" aria-labelledby="riders-h">
      <h3 id="riders-h" className="pmu-c-h3">Riders ({riders.length})</h3>
      {act.error && <Banner tone="danger" title={act.error} />}
      {pickups.length === 0 ? <p className="pmu-muted">Add the pick-up stops first.</p> : <AddRider path={path} o={o} stops={pickups} />}
      <Table
        caption="Riders" hideCaption rowKey={(r) => r.id} empty="No riders yet."
        rows={[...riders].sort((a, b) => stops.findIndex((s) => s.id === a.stop_id) - stops.findIndex((s) => s.id === b.stop_id) || (a.child?.last ?? "").localeCompare(b.child?.last ?? ""))}
        columns={[
          { key: "child", header: "Child", rowHeader: true, render: (r) => (r.child ? fullName(r.child) : "No longer on the roster") },
          { key: "stop", header: "Stop", render: (r) => stops.find((s) => s.id === r.stop_id)?.name ?? "" },
          { key: "meet", header: "At the stop", render: (r) => (
            <span className="pmu-stack pmu-c-tight">
              <span>{r.meet_required ? "An adult meets them" : "May walk home"}</span>
              {(r.meet_by === "parent" || r.meet_by === "office") && <span className="pmu-small pmu-muted">{r.meet_by === "parent" ? "Parent's choice" : "Office's note"}{r.meet_reason ? `: “${r.meet_reason}”` : ""}</span>}
            </span>
          ) },
          { key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (r) => <Button size="sm" variant="ghost" disabled={act.busy} onClick={() => act.run(() => api("DELETE", `${path}/riders/${r.id}`), { success: "Rider removed.", invalidate: [path] })}>Remove</Button> },
        ]}
      />
    </section>
  );
}

function AddRider({ path, o, stops }: { path: string; o: Overview; stops: (Stop & { id: string })[] }) {
  const [siteId, setSiteId] = useState(o.sites[0]?.id ?? "");
  // Names and room only, for own schools and contracted ones (a bus company has no roster access otherwise).
  const kids = useGet<{ children: { id: string; first: string; last: string }[] }>(siteId ? `${path.slice(0, path.indexOf("/routes/"))}/sites/${siteId}/children` : null);
  // "rule": the school's grade rule decides (the server applies it); parents can choose otherwise later.
  const [f, setF] = useState<{ child_id: string; stop_id: string; meet: "rule" | "yes" | "no" }>({ child_id: "", stop_id: stops[0]?.id ?? "", meet: "rule" });
  const act = useAction();
  return (
    <form className="pmu-row" onSubmit={(e) => { e.preventDefault(); if (f.child_id) void act.run(() => api("POST", `${path}/riders`, { site_id: siteId, child_id: f.child_id, stop_id: f.stop_id, ...(f.meet === "rule" ? {} : { meet_required: f.meet === "yes" }) }), { success: "Rider added.", invalidate: [path] }).then((r) => { if (r) setF({ ...f, child_id: "" }); }); }}>
      {o.sites.length > 1 && <Select label="School" value={siteId} onValueChange={(v) => { setSiteId(v ?? ""); setF({ ...f, child_id: "" }); }} options={o.sites.map((s) => ({ value: s.id, label: s.name }))} />}
      <Select label="Child" value={f.child_id} onValueChange={(v) => setF({ ...f, child_id: v ?? "" })} options={(kids.data?.children ?? []).map((c) => ({ value: c.id, label: fullName(c) }))} />
      <Select label="Stop" value={f.stop_id} onValueChange={(v) => setF({ ...f, stop_id: v ?? "" })} options={stops.map((s) => ({ value: s.id, label: s.name }))} />
      <Select label="At the stop" value={f.meet} onValueChange={(v) => setF({ ...f, meet: (v ?? "rule") as typeof f.meet })}
        options={[{ value: "rule", label: "The school's rule (by grade)" }, { value: "yes", label: "An adult meets them" }, { value: "no", label: "May walk home" }]} />
      <Button type="submit" size="sm" loading={act.busy} disabled={!f.child_id || !f.stop_id}>Add</Button>
      {act.error && <Banner tone="danger" title={act.error} />}
    </form>
  );
}

/** Add a vehicle, or change one's name, plate and seats (`v`). */
function VehicleDialog({ base, v, trigger }: { base: string; v?: Vehicle; trigger: ReactElement }) {
  const act = useAction();
  const [open, setOpen] = useState(false);
  const blank = () => ({ name: v?.name ?? "", plate: v?.plate ?? "", seats: v?.seats != null ? String(v.seats) : "" });
  const [f, setF] = useState(blank);
  const save = () => act.run(() => api(v ? "PUT" : "POST", v ? `${base}/vehicles/${v.id}` : `${base}/vehicles`, { name: f.name.trim(), plate: f.plate.trim() || null, seats: f.seats ? Number(f.seats) : null }),
    { success: v ? `${f.name.trim()} saved.` : "Vehicle added.", invalidate: [base] }).then((r) => { if (r) setOpen(false); });
  return (
    <Dialog title={v ? `Edit ${v.name}` : "Add a vehicle"} open={open} onOpenChange={(x) => { setOpen(x); if (x) setF(blank()); }} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!f.name.trim()} onClick={() => void save()}>{v ? "Save" : "Add"}</Button></>}>
      <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (f.name.trim()) void save(); }}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <TextField label="Name" required placeholder="Bus 7" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <TextField label="Plate" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} />
        <TextField label="Seats" type="number" inputMode="numeric" min={1} value={f.seats} onChange={(e) => setF({ ...f, seats: e.target.value })} />
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

function Vehicles({ base, o }: { base: string; o: Overview }) {
  const act = useAction();
  return (
    <div className="pmu-stack">
      <Toolbar actions={<VehicleDialog base={base} trigger={<Button icon="plus">Add a vehicle</Button>} />} />
      {act.error && <Banner tone="danger" title={act.error} />}
      <Table
        caption="Vehicles" hideCaption rowKey={(v) => v.id} rows={o.vehicles} empty="No vehicles yet."
        columns={[
          { key: "name", header: "Vehicle", rowHeader: true, render: (v) => <VehicleDialog base={base} v={v} trigger={<button type="button" className="pmu-c-linkbutton">{v.name}</button>} /> },
          { key: "plate", header: "Plate", render: (v) => v.plate ?? "" },
          { key: "seats", header: "Seats", align: "end", render: (v) => v.seats ?? "" },
          { key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (v) => (
            <span className="pmu-row ptr-vehicle-acts">
              <VehicleDialog base={base} v={v} trigger={<Button size="sm" variant="ghost">Edit</Button>} />
              <ConfirmButton size="sm" variant="ghost" danger title={`Remove ${v.name}?`} description="Routes using it will have no vehicle until you choose another." confirmLabel="Remove"
                onConfirm={() => act.run(() => api("DELETE", `${base}/vehicles/${v.id}`), { success: `${v.name} removed.`, invalidate: [base] })}>Remove</ConfirmButton>
            </span>
          ) },
        ]}
      />
    </div>
  );
}

interface Swap {
  id: string; vehicle_id: string; route_id: string | null; with_vehicle_id: string | null; with_name: string | null; with_plate: string | null;
  from_date: string; to_date: string; reason: string | null; routes: string[]; children: number; schools: number; in_force: boolean;
}
const shortDay = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });

/**
 * A bus off the road (PMU-232): another bus from the fleet, or a hired one, runs its routes for a
 * day or a period. Parents of its riders and each school's administrators are told when it is set
 * and when the usual bus is back.
 */
function Swaps({ base, o }: { base: string; o: Overview }) {
  const q = useGet<{ today: string; swaps: Swap[] }>(`${base}/swaps`);
  const act = useAction();
  const [open, setOpen] = useState(false);
  const blank = () => ({ vehicle_id: o.vehicles[0]?.id ?? "", route_id: "", with: "", with_name: "", with_plate: "", from_date: q.data?.today ?? "", to_date: q.data?.today ?? "", reason: "" });
  const [f, setF] = useState(blank);
  const name = (id: string | null) => o.vehicles.find((v) => v.id === id)?.name ?? "A removed bus";
  const itsRoutes = o.routes.filter((r) => r.vehicle_id === f.vehicle_id);
  const hired = f.with === "hired";
  const ready = f.vehicle_id && itsRoutes.length && (hired ? f.with_name.trim() : f.with) && f.from_date && f.to_date >= f.from_date;
  const save = () => act.run(() => api<{ routes: string[]; children: number }>("POST", `${base}/swaps`, {
    vehicle_id: f.vehicle_id, route_id: f.route_id || null, from_date: f.from_date, to_date: f.to_date, reason: f.reason || null,
    ...(hired ? { with_name: f.with_name, with_plate: f.with_plate || null } : { with_vehicle_id: f.with }),
  }), { success: "Saved. Parents and schools are being told.", invalidate: [`${base}/swaps`, base] }).then((r) => { if (r) setOpen(false); });
  if (q.loading) return <Loading />;
  if (q.error) return <LoadError error={q.error} retry={q.reload} />;
  const { today, swaps } = q.data!;
  return (
    <div className="pmu-stack">
      <Toolbar actions={
        <Dialog title="Replace a bus" description="For a breakdown, a repair or an inspection: another bus runs its routes on those days. Parents of its riders and each school's office are told."
          open={open} onOpenChange={(v) => { setOpen(v); if (v) setF(blank()); }} trigger={<Button icon="plus" disabled={o.vehicles.length < 1}>Replace a bus</Button>}
          actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!ready} onClick={() => void save()}>Save and tell parents</Button></>}>
          <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (ready) void save(); }}>
            {act.error && <Banner tone="danger" title={act.error} />}
            <Select label="The bus off the road" value={f.vehicle_id} onValueChange={(v) => setF({ ...f, vehicle_id: v ?? "", route_id: "", with: f.with === v ? "" : f.with })}
              options={o.vehicles.map((v) => ({ value: v.id, label: `${v.name}${v.plate ? ` · ${v.plate}` : ""}` }))} />
            {!itsRoutes.length ? <Banner tone="info" title="No route uses this bus">Choose the bus that runs the route.</Banner> : (
              <Select label="Routes" value={f.route_id || "all"} onValueChange={(v) => setF({ ...f, route_id: !v || v === "all" ? "" : v })}
                options={[{ value: "all", label: itsRoutes.length === 1 ? itsRoutes[0]!.name : `All its routes (${itsRoutes.map((r) => r.name).join(", ")})` }, ...(itsRoutes.length > 1 ? itsRoutes.map((r) => ({ value: r.id, label: `Only ${r.name}` })) : [])]} />
            )}
            <Select label="The bus standing in" value={f.with} onValueChange={(v) => setF({ ...f, with: v ?? "" })} placeholder="Choose a bus"
              options={[...o.vehicles.filter((v) => v.id !== f.vehicle_id).map((v) => ({ value: v.id, label: `${v.name}${v.plate ? ` · ${v.plate}` : ""}` })), { value: "hired", label: "Another bus (hired or borrowed)" }]} />
            {hired && (
              <div className="pmu-row">
                <TextField label="Its name" required placeholder="Coach from Lone Star Charters" value={f.with_name} onChange={(e) => setF({ ...f, with_name: e.target.value })} />
                <TextField label="Plate" hint="Parents look for it" value={f.with_plate} onChange={(e) => setF({ ...f, with_plate: e.target.value })} />
              </div>
            )}
            <div className="pmu-row">
              <TextField label="First day" type="date" required min={today} value={f.from_date} onChange={(e) => setF({ ...f, from_date: e.target.value, to_date: f.to_date < e.target.value ? e.target.value : f.to_date })} />
              <TextField label="Last day" type="date" required min={f.from_date || today} value={f.to_date} onChange={(e) => setF({ ...f, to_date: e.target.value })} />
            </div>
            <TextField label="Why (parents see it)" maxLength={200} placeholder="Bus 7 is in for brake repairs." value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />
            <button type="submit" hidden />
          </form>
        </Dialog>
      } />
      <Table
        caption="Bus changes" hideCaption rowKey={(x) => x.id} rows={swaps} empty="No bus changes now or coming. When a bus is off the road, replace it here and parents and schools are told."
        columns={[
          { key: "bus", header: "Bus", rowHeader: true, render: (x) => <span className="pmu-stack pmu-c-tight"><b>{x.with_vehicle_id ? name(x.with_vehicle_id) : `${x.with_name}${x.with_plate ? ` · ${x.with_plate}` : ""}`}</b><span className="pmu-small pmu-muted">instead of {name(x.vehicle_id)}</span></span> },
          { key: "routes", header: "Routes", render: (x) => x.routes.join(", ") || "None now" },
          { key: "days", header: "Days", render: (x) => <span className="pmu-row">{x.from_date === x.to_date ? shortDay(x.from_date) : `${shortDay(x.from_date)} to ${shortDay(x.to_date)}`}{x.in_force && <Badge tone="info">Now</Badge>}</span> },
          { key: "who", header: "Told", render: (x) => `${x.children} ${x.children === 1 ? "child" : "children"} at ${x.schools} ${x.schools === 1 ? "school" : "schools"}` },
          { key: "why", header: "Why", render: (x) => x.reason ?? "" },
          { key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (x) => (
            <ConfirmButton size="sm" variant="secondary" title={x.in_force ? `${name(x.vehicle_id)} is back?` : "Call off this change?"}
              description={x.in_force ? `From today the route runs with ${name(x.vehicle_id)} again. Parents and schools are told.` : "It has not started. Parents and schools are told it is off."}
              confirmLabel={x.in_force ? "It is back" : "Call it off"}
              onConfirm={() => act.run(() => api("DELETE", `${base}/swaps/${x.id}`), { success: "Done. Parents and schools are being told.", invalidate: [`${base}/swaps`] })}>{x.in_force ? `${name(x.vehicle_id)} is back` : "Call off"}</ConfirmButton>
          ) },
        ]}
      />
    </div>
  );
}

/**
 * Contracts between schools and bus companies (slice 5). A school adds its bus company with the
 * code the company gives it; a bus company sees its schools and its code. Either side can end one.
 */
function Contracts({ base, sites }: { base: string; sites: Overview["sites"] }) {
  const { site } = useConsole();
  const path = `${base}/contracts`;
  const d = useGet<{ kind: string; contracts: Contract[]; link_code: string | null }>(path);
  const act = useAction();
  const [code, setCode] = useState("");
  const [siteId, setSiteId] = useState(sites.find((s) => s.id === site.id)?.id ?? sites[0]?.id ?? "");
  if (d.loading) return <Loading />;
  if (d.error) return <LoadError error={d.error} retry={d.reload} />;
  const operator = d.data!.kind === "operator";
  const active = d.data!.contracts.filter((k) => k.status === "active");
  return (
    <div className="pmu-stack">
      {act.error && <Banner tone="danger" title={act.error} />}
      {operator ? (
        <section className="pmu-stack" aria-labelledby="code-h">
          <h3 id="code-h" className="pmu-c-h3">Your code for schools</h3>
          <p className="pmu-muted">A school enters this code in its Vavr console (Buses, Bus companies) to let your routes carry its children. You see a school's riders only while it has you under contract.</p>
          {d.data!.link_code ? <p className="pmu-c-code">{d.data!.link_code}</p> : <p className="pmu-muted">No code yet.</p>}
          <div><Button variant="secondary" loading={act.busy} onClick={() => act.run(() => api("POST", `${base}/link-code`), { success: "New code made. The old one no longer works.", invalidate: [path] })}>{d.data!.link_code ? "Make a new code" : "Make a code"}</Button></div>
        </section>
      ) : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); if (code.trim()) void act.run(() => api<{ operator: string }>("POST", path, { code, site_id: siteId }), { success: "Bus company added.", invalidate: [path, base] }).then((r) => { if (r) setCode(""); }); }}>
          <h3 className="pmu-c-h3">Add a bus company</h3>
          <p className="pmu-muted">Enter the code the bus company gave you. Its routes can then carry your children, and its drivers record who gets on and off in your records. They see your riders' names and stops only, never the rest of your records. You can end it at any time.</p>
          <div className="pmu-row">
            {sites.length > 1 && <Select label="School" value={siteId} onValueChange={(v) => setSiteId(v ?? "")} options={sites.map((s) => ({ value: s.id, label: s.name }))} />}
            <TextField label="Bus company's code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="K7M2QX9P" />
            <Button type="submit" loading={act.busy} disabled={!code.trim() || !siteId}>Add</Button>
          </div>
        </form>
      )}
      <Table
        caption={operator ? "Schools" : "Bus companies"} rowKey={(k) => k.id} rows={active} empty={operator ? "No school has added you yet." : "No bus company yet."}
        columns={[
          { key: "who", header: operator ? "School" : "Bus company", rowHeader: true, render: (k) => (operator ? `${k.school}${k.school_org && k.school_org !== k.school ? ` (${k.school_org})` : ""}` : `${k.operator} for ${k.school}`) },
          { key: "since", header: "Since", render: (k) => new Date(k.created_at).toLocaleDateString() },
          { key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (k) => (
            <ConfirmButton size="sm" variant="ghost" danger title="End this contract?" description={operator ? "Your routes stop carrying this school's children at once, and its riders come off your routes." : "The bus company's routes stop carrying your children at once, and they come off its routes."} confirmLabel="End"
              onConfirm={() => act.run(() => api("DELETE", `${path}/${k.id}`), { success: "Contract ended.", invalidate: [path, base] })}>End</ConfirmButton>
          ) },
        ]}
      />
    </div>
  );
}

/** A stop's planned "15:30" as "3:30 PM". */

/** Today's runs, live (slice 6): refreshed every 20 s while the tab is open. */
/**
 * Today's overview: the numbers at a glance, every route and bus on one map (choose a bus for its
 * details), then each route's card. The Bus map tab has the same map, larger, with the list of buses.
 */
function Today({ base }: { base: string }) {
  const { site } = useConsole();
  const { live, shapes, now } = useFleet(base);
  const [selected, setSelected] = useState<string | null>(null);
  const selRun = live.data?.routes.find((r) => r.id === selected)?.run;
  const record = useRunRecord(base, selRun?.id, !!selRun && !selRun.finished_at);
  if (live.loading && !live.data) return <Loading />;
  if (live.error && !live.data) return <LoadError error={live.error} retry={live.reload} />;
  const routes = live.data?.routes ?? [];
  const tz = live.data?.tz ?? site.tz;
  if (!routes.length) return <p className="pmu-muted">No route runs today.</p>;
  const sel = routes.find((r) => r.id === selected);
  return (
    <div className="pmu-stack">
      <FleetSummary routes={routes} now={now} />
      <FleetMap routes={routes} shapes={shapes} now={now} selected={selected} onSelect={setSelected} height={440} label="Today's routes and buses" record={record} />
      {sel && <BusDetail r={sel} tz={tz} now={now} onClose={() => setSelected(null)} />}
      <div className="pmt-live">
        {routes.map((r) => {
          const run = r.run;
          const s = busStatus(r, now);
          return (
            <section key={r.id} className="pmt-livecard" aria-label={r.name} data-selected={r.id === selected ? "" : undefined}>
              <header className="pmt-livecard__head">
                <div><h3 className="pmt-card__title"><button type="button" className="pmu-c-linkbutton" onClick={() => setSelected(r.id === selected ? null : r.id)}>{r.name}</button></h3><p className="pmu-small pmu-muted">{busText(r)}{run?.driver ? ` · ${run.driver}` : r.assigned_driver ? ` · ${r.assigned_driver}` : ""} · {r.riders} children</p></div>
                <span className="pmu-row"><Badge tone={s.tone}>{run?.finished_at ? `Finished ${time(run.finished_at, tz)}` : s.text}</Badge></span>
              </header>
              {run && (
                <>
                  <div className="pmt-progress" role="img" aria-label={`Stop ${run.reached_index + 1} of ${r.stops}`}>
                    {Array.from({ length: r.stops }, (_, i) => <span key={i} className="pmt-progress__stop" data-done={i <= run.reached_index ? "" : undefined} />)}
                  </div>
                  <dl className="pmt-livefacts">
                    <div><dt>On board</dt><dd>{run.on_board}</dd></div>
                    <div><dt>Off</dt><dd>{run.off}</dd></div>
                    <div><dt>Still to board</dt><dd>{run.waiting}</dd></div>
                    {!run.finished_at && <div><dt>Next stop</dt><dd>{run.next ? `${run.next.name}${run.next.eta_at ? `, ${time(run.next.eta_at, tz)}` : ""}` : "School"}</dd></div>}
                  </dl>
                </>
              )}
              {(r.exceptions ?? []).length > 0 && (
                <ul className="pmt-exceptions" aria-label="Exceptions">
                  {r.exceptions!.map((x, i) => <li key={i}><Badge tone={x.what === "absent" ? "warning" : "danger"}>{time(x.at, tz)}</Badge> {x.child}: {EXCEPTION[x.what]}{x.stop ? `, ${x.stop}` : ""}</li>)}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** The Bus tab's top bar: the driver's own schedule and time off (PMU-254). */
function ScheduleButton() {
  const path = usePath();
  if (path.startsWith(`${T}/bus/schedule`)) return null;
  return <IconButton icon="calendar" label="My schedule" onClick={() => navigate(`${T}/bus/schedule`)} />;
}

export const transportUi: UiPart = {
  id: "transport",
  slots: {
    "console.nav": [...TRANSPORT_PAGES.map((p) => ({ id: p.page === "live" ? "transport" : `transport-${p.page}`, label: p.label, icon: p.icon, path: p.path, title: p.label, group: "Transport", permission: "transport:manage" as const, render: () => <Transport page={p.page} /> })),
      { id: "buses", label: "Buses", icon: "bus", path: "buses", title: "Buses", permission: "site.configure", orgKind: "school", render: () => <SchoolBuses /> }],
    "console.search": [{
      id: "transport", label: "Routes and buses", permission: "transport:manage", path: (site) => `/api/v1/orgs/${site.org_id}/transport`,
      items: (o: Overview) => [
        ...o.routes.map((r) => ({ id: r.id, label: r.name, detail: `Route · ${KIND[r.kind]} · ${r.stops} stops · ${r.riders} riders`, icon: "route" as const, href: "/console/transport/fleet?view=routes" })),
        ...o.vehicles.map((v) => ({ id: v.id, label: v.name, detail: ["Bus", v.plate, v.seats ? `${v.seats} seats` : null].filter(Boolean).join(" · "), icon: "bus" as const, href: "/console/transport/fleet?view=vehicles" })),
      ],
    }],
    "staff.tab": [{ id: "bus", label: "Bus", icon: "bus", path: "bus", title: "Today's buses", after: "class", permission: "transport:ride", topBar: () => <ScheduleButton />, render: () => <BusPanel /> }],
    "staff.page": [{ id: "bus-schedule", path: "bus/schedule", title: "My schedule", tab: "bus", permission: "transport:ride", render: () => <MySchedule /> }],
    "guardian.home": [{ id: "bus", render: () => <BusCard /> }],
    // Before "rides/:site/:run/:child" would match "rides": the list's path is exact.
    "guardian.page": [
      { id: "bus", path: "bus/:site/:child/:route", title: "The bus", render: (p) => <BusPage site={p.site!} child={p.child!} route={p.route!} /> },
      { id: "ride", path: "rides/:site/:run/:child", title: "The ride", render: (p) => <RidePage site={p.site!} run={p.run!} child={p.child!} /> },
      { id: "rides", path: "rides", title: "Past rides", render: () => <RidesPage /> },
    ],
  },
};
